import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeft, Pencil, Printer } from 'lucide-react';
import useUIStore from '../../../store/useUIStore';

const OrderDetailPage = ({
  orderId = '',
  sess = { uid: '', role: 'user' },
}) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const { showSuccess, showError } = useUIStore();

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch('/chatbot/api/v1.0/order/details', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, uid: sess.uid }),
      });
      const result = await response.json();
      if (result?.data) {
        setOrder(result.data);
      } else {
        showError('Order not found');
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      showError('Error loading order details');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ffc107',
      confirmed: '#17a2b8',
      processing: '#007bff',
      shipped: '#6f42c1',
      delivered: '#28a745',
      cancelled: '#dc3545',
    };
    return colors[status] || '#6c757d';
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) {
      showError('Please select a status');
      return;
    }

    try {
      const response = await fetch('/chatbot/api/v1.0/order/status/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, status: newStatus, note: statusNote }),
      });
      const result = await response.json();
      if (result.success) {
        showSuccess('Status updated successfully');
        setShowStatusModal(false);
        fetchOrderDetails();
      } else {
        showError('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showError('Error updating status');
    }
  };

  const printInvoice = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
        <Typography variant="h5">Order not found</Typography>
        <Button
          variant="contained"
          onClick={() => {
            window.location.href = '/orders';
          }}
        >
          Back to Orders
        </Button>
      </Stack>
    );
  }

  const detailItems = [
    { label: 'Order ID', value: <Typography fontWeight={700}>{order.order_id}</Typography> },
    { label: 'Order Date', value: formatDateTime(order.created_at) },
    {
      label: 'Status',
      value: (
        <Chip
          label={order.status || 'pending'}
          sx={{
            textTransform: 'capitalize',
            color: '#fff',
            backgroundColor: getStatusColor(order.status),
          }}
        />
      ),
    },
    {
      label: 'Payment Method',
      value: order.payment_method || 'Cash on Delivery',
    },
    {
      label: 'Payment Status',
      value: (
        <Chip
          label={order.payment_status || 'pending'}
          color={order.payment_status === 'paid' ? 'success' : 'warning'}
          size="small"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
  ];

  const totals = [
    { label: 'Subtotal', value: parseFloat(order.subtotal || 0).toFixed(2) },
    order.discount > 0 && {
      label: 'Discount',
      value: `-${parseFloat(order.discount).toFixed(2)}`,
      color: 'success.main',
    },
    order.tax > 0 && { label: 'Tax', value: parseFloat(order.tax).toFixed(2) },
    order.shipping_charge > 0 && {
      label: 'Shipping',
      value: parseFloat(order.shipping_charge).toFixed(2),
    },
    {
      label: 'Total',
      value: parseFloat(order.total_amount).toFixed(2),
      bold: true,
      color: 'primary.main',
    },
  ].filter(Boolean);

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ '@media print': { display: 'none' } }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              startIcon={<ArrowLeft size={18} />}
              onClick={() => {
                window.location.href = '/orders';
              }}
              size="small"
            >
              Back
            </Button>
            <Typography variant="h6" component="h1">
              Order Details - {order.order_id}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<Pencil size={16} />}
              onClick={() => setShowStatusModal(true)}
              size="small"
            >
              Update Status
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<Printer size={16} />}
              onClick={printInvoice}
              size="small"
            >
              Print Invoice
            </Button>
            <Button
              variant="outlined"
              startIcon={<Pencil size={16} />}
              onClick={() => {
                window.location.href = `/orders/edit/${orderId}`;
              }}
              size="small"
            >
              Edit Order
            </Button>
          </Stack>
        </Stack>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Order Information
            </Typography>
            <Stack spacing={1} divider={<Divider />}>
              {detailItems.map((item) => (
                <Stack
                  key={item.label}
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  spacing={1}
                  py={0.5}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body1">{item.value}</Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Customer Information
            </Typography>
            <Stack spacing={1} divider={<Divider />}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                spacing={1}
                py={0.5}
              >
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
                  Customer Name
                </Typography>
                <Typography>{order.customer_name || '-'}</Typography>
              </Stack>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                spacing={1}
                py={0.5}
              >
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
                  Contact Number
                </Typography>
                <Typography>{order.customer_number || '-'}</Typography>
              </Stack>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                spacing={1}
                py={0.5}
              >
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
                  Email
                </Typography>
                <Typography>{order.customer_email || '-'}</Typography>
              </Stack>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                spacing={1}
                py={0.5}
              >
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
                  Delivery Address
                </Typography>
                <Typography>
                  {order.delivery_address || '-'}
                  {order.city && `, ${order.city}`}
                  {order.state && `, ${order.state}`}
                  {order.pincode && ` - ${order.pincode}`}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Order Items
            </Typography>
            {order.items && order.items.length > 0 ? (
              <Stack spacing={1} divider={<Divider />}>
                {order.items.map((item, index) => (
                  <Stack
                    key={`${item.product_name}-${index}`}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Stack spacing={0.5}>
                      <Typography fontWeight={600}>{item.product_name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.product_description}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Quantity: {item.quantity} • ${parseFloat(item.price).toFixed(2)}
                      </Typography>
                    </Stack>
                    <Typography fontWeight={700}>
                      ${(item.quantity * parseFloat(item.price)).toFixed(2)}
                    </Typography>
                  </Stack>
                ))}
                <Box pt={1}>
                  <Stack spacing={1}>
                    {totals.map((row, idx) => (
                      <Stack
                        key={`${row.label}-${idx}`}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          pt: idx === totals.length - 1 ? 1 : 0,
                          borderTop: idx === totals.length - 1 ? 1 : 0,
                          borderColor: idx === totals.length - 1 ? 'divider' : 'transparent',
                        }}
                      >
                        <Typography fontWeight={row.bold ? 700 : 400}>{row.label}:</Typography>
                        <Typography
                          fontWeight={row.bold ? 700 : 500}
                          sx={{ color: row.color }}
                        >
                          ${row.value}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No items in this order
              </Typography>
            )}
          </CardContent>
        </Card>

        {order.timeline && order.timeline.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Timeline
              </Typography>
              <Stack spacing={2} sx={{ borderLeft: 2, borderColor: 'divider', pl: 2 }}>
                {order.timeline.map((event, index) => (
                  <Stack key={`${event.status}-${index}`} spacing={0.5} position="relative">
                    <Box
                      sx={{
                        position: 'absolute',
                        left: -29,
                        top: 4,
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        border: '2px solid white',
                        boxShadow: (theme) => `0 0 0 2px ${theme.palette.success.main}`,
                      }}
                    />
                    <Typography fontWeight={600}>{event.status}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDateTime(event.timestamp)}
                    </Typography>
                    {event.note && (
                      <Typography variant="body2" color="text.secondary">
                        Note: {event.note}
                      </Typography>
                    )}
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

        {order.notes && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Notes
              </Typography>
              <Typography>{order.notes}</Typography>
            </CardContent>
          </Card>
        )}
      </Stack>

      <Dialog
        open={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-label">New Status</InputLabel>
              <Select
                labelId="status-label"
                value={newStatus}
                label="New Status"
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <MenuItem value="">Select Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="shipped">Shipped</MenuItem>
                <MenuItem value="delivered">Delivered</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Note (Optional)"
              multiline
              minRows={3}
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              placeholder="Add any notes about this status change..."
              size="small"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleStatusUpdate}>
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderDetailPage;
